(function () {
  'use strict';

  /*
   * Breakpoint-based desktop / mobile header switch.
   * No dynamic measurement. No ResizeObserver. No flickering.
   *
   * Above BREAKPOINT → Desktop Header  (data-menu-style="menu")
   * Below BREAKPOINT → Mobile Header   (data-menu-style="drawer")
   *
   * window.matchMedia fires ONCE per breakpoint crossing, never on DOM/layout
   * changes — this is what prevents the measurement-loop flicker.
   */

  var BREAKPOINT = 1100; /* px — adjust here if switch happens too early/late */

  var header = document.getElementById('header-component');
  if (!header) return;

  var mq = window.matchMedia('(min-width: ' + BREAKPOINT + 'px)');

  function getOL() { return header.querySelector('overflow-list'); }

  /* ── overflow-list item helpers ──────────────────────────────────────────── */

  /* Returns the menu <li> items (excludes the light-DOM MORE button). */
  function getMenuItems(ol) {
    return Array.prototype.slice.call(
      ol.querySelectorAll('li.menu-list__list-item:not([slot="more"])')
    );
  }

  /* True if any menu item has been moved to the overflow slot by #reflowItems(). */
  function hasItemsInOverflow(ol) {
    return getMenuItems(ol).some(function (li) { return li.hasAttribute('slot'); });
  }

  /* Move all menu items back to the default slot (removes slot="overflow"). */
  function restoreItems(ol) {
    getMenuItems(ol).forEach(function (li) { li.removeAttribute('slot'); });
  }

  /*
   * #reflowItems() sets flex-wrap:wrap and overflow:unset as inline styles on
   * [part="list"] and never removes them. Without cleanup:
   *   - flex-wrap:wrap can cause items to stack onto a second row
   *   - overflow:unset removes the scroll containment
   * Removing these lets the CSS defaults (nowrap, overflow-x:auto) take over.
   */
  function cleanListStyles(ol) {
    var list = ol.shadowRoot && ol.shadowRoot.querySelector('[part="list"]');
    if (!list) return;
    list.style.removeProperty('flex-wrap');
    list.style.removeProperty('overflow');
    list.style.removeProperty('height');
  }

  /*
   * Robustly disable overflow-list so it never moves items to the overflow slot.
   *
   * Timing problem:
   *   overflow-list.js#initialize() is async (waits for its stylesheet to load).
   *   Our first call to setAttribute('disabled','true') may happen before #refs
   *   are set inside the custom element, causing its #reset() to throw internally
   *   (browser silently catches it — our try/catch never sees it). The attribute
   *   IS set, but #reset() didn't run, so observers are still active.
   *
   *   After #initialize() completes, the IntersectionObserver fires and calls
   *   #reflowItems() synchronously. At that moment #refs ARE set, so #reflowItems()
   *   runs successfully — and with the CSS giving the center column space it may
   *   still wrap items if the grid hasn't fully painted, moving them to
   *   slot="overflow" (display:none), making the menu invisible.
   *
   * Fix:
   *   Detect that items landed in overflow (which proves #refs are now set), then
   *   cycle the disabled attribute: remove it (triggers #reflowItems() which NOW
   *   works) then immediately re-set it (triggers #reset() which NOW works).
   *   Also restore items directly and clean up stale inline styles.
   */
  function disableOL() {
    var ol = getOL();
    if (!ol) { requestAnimationFrame(disableOL); return; }

    if (hasItemsInOverflow(ol)) {
      /*
       * Items are in the overflow slot — #reflowItems() has run and refs are set.
       * Cycling disabled triggers a clean #reset() that moves items back and
       * disconnects observers so #reflowItems() can never run again.
       */
      if (ol.hasAttribute('disabled')) ol.removeAttribute('disabled');
      ol.setAttribute('disabled', 'true');
      /* Belt-and-suspenders: also restore directly in case of another race. */
      restoreItems(ol);
      cleanListStyles(ol);
    } else {
      /* Items are in the default slot (either not yet touched, or already clean). */
      if (!ol.hasAttribute('disabled')) {
        ol.setAttribute('disabled', 'true');
      }
      /*
       * Wait one frame: #reflowItems() may fire in this frame (from the
       * IntersectionObserver), moving items to overflow. Catch it next frame.
       */
      requestAnimationFrame(function () {
        if (!mq.matches) return; /* switched to mobile in the meantime */
        var ol2 = getOL();
        if (!ol2) return;
        if (hasItemsInOverflow(ol2)) {
          ol2.removeAttribute('disabled');
          ol2.setAttribute('disabled', 'true');
          restoreItems(ol2);
        }
        cleanListStyles(ol2);
      });
    }
  }

  /* ── State setters — always apply, no early returns ─────────────────────── */

  function showDesktop() {
    var ol = getOL();
    if (ol) ol.removeAttribute('minimum-reached');
    header.dataset.menuStyle = 'menu';
    disableOL();
  }

  function showMobile() {
    var ol = getOL();
    if (ol) ol.setAttribute('minimum-reached', '');
    header.dataset.menuStyle = 'drawer';
  }

  /* ── Guards ──────────────────────────────────────────────────────────────── */

  /*
   * Guard A: minimum-reached
   *
   * overflow-list can fire overflowMinimum(true) between DOMContentLoaded and
   * full initialisation. header.js sees this, sets minimum-reached, and
   * setHeaderMenuStyle() flips data-menu-style to 'drawer'. Detect and revert.
   */
  function watchMinimumReached() {
    var ol = getOL();
    if (!ol) return;
    new MutationObserver(function () {
      if (mq.matches && ol.hasAttribute('minimum-reached')) {
        ol.removeAttribute('minimum-reached');
        header.dataset.menuStyle = 'menu';
      }
    }).observe(ol, { attributes: true, attributeFilter: ['minimum-reached'] });
  }

  /*
   * Guard B: slot attributes
   *
   * If a late-scheduled #reflowItems() (e.g. from a pending RAF when we called
   * removeAttribute('disabled') during the cycle) moves items to slot="overflow",
   * restore them. Debounced via ticking flag to avoid re-entrancy.
   */
  function watchItemSlots() {
    var ol = getOL();
    if (!ol) return;
    var ticking = false;
    new MutationObserver(function () {
      if (!mq.matches || ticking) return;
      ticking = true;
      requestAnimationFrame(function () {
        ticking = false;
        if (!mq.matches) return;
        var ol2 = getOL();
        if (ol2 && hasItemsInOverflow(ol2)) {
          restoreItems(ol2);
          cleanListStyles(ol2);
        }
      });
    }).observe(ol, { subtree: true, attributes: true, attributeFilter: ['slot'] });
  }

  /* ── Core ────────────────────────────────────────────────────────────────── */

  function applyState(matches) {
    if (matches) {
      showDesktop();
      /*
       * Second pass after 2 frames: catches late overflowMinimum events that
       * fire after DOMContentLoaded as the custom element finishes initialising.
       */
      requestAnimationFrame(function () {
        requestAnimationFrame(function () {
          if (mq.matches) showDesktop();
        });
      });
    } else {
      showMobile();
    }
  }

  function onBreakpointChange(e) { applyState(e.matches); }

  if (mq.addEventListener) {
    mq.addEventListener('change', onBreakpointChange);
  } else {
    mq.addListener(onBreakpointChange); /* Safari < 14 fallback */
  }

  document.addEventListener('shopify:section:load', function () {
    applyState(mq.matches);
  });

  function init() {
    disableOL();
    watchMinimumReached();
    watchItemSlots();
    applyState(mq.matches);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
